import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/dashboard/Sidebar';

export default function Gatekeeper() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [tab, setTab] = useState('pending');
  const [aiExplanation, setAiExplanation] = useState(null);
  const [explaining, setExplaining] = useState(false);

  useEffect(() => { loadItems(); }, [tab]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/sync/gatekeeper?status=${tab}`);
      setItems(res.data);
    } catch (err) { /* error handled silently */ }
    finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    setActing(true);
    try {
      await axios.patch(`/api/sync/gatekeeper/${id}`, { action, notes });
      setSelected(null);
      setNotes('');
      loadItems();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setActing(false);
    }
  };

  const explainItem = async (item) => {
    setExplaining(true);
    setAiExplanation(null);
    try {
      const res = await axios.post('/api/sync/explain-error', {
        errorData: item.source_data
      });
      setAiExplanation(res.data.explanation);
    } catch {}
    finally { setExplaining(false); }
  };

  return (
    <div className="app-layout">
      <Sidebar pendingApprovals={tab === 'pending' ? items.length : 0} />
      <main className="app-main">
        <div className="page-header">
          <div>
            <h2 className="page-title">Approvals</h2>
            <p className="page-sub">Review and approve items before they sync to QuickBooks</p>
          </div>
        </div>

        <div className="tabs">
          {['pending', 'approved', 'rejected'].map(t => (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => { setTab(t); setSelected(null); }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
              {t === 'pending' && items.length > 0 && tab === 'pending' && (
                <span className="badge badge-warning" style={{ marginLeft: 6 }}>
                  {items.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="grid-2" style={{ gap: 16, alignItems: 'flex-start' }}>
          {/* Item List */}
          <div className="card card-glass" style={{ padding: 0 }}>
            {loading ? (
              <div className="loading"><div className="spinner" /></div>
            ) : items.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">✦</div>
                <h3 className="empty-state-title">{tab === 'pending' ? 'No items pending review' : `No ${tab} items`}</h3>
                <p className="empty-state-desc">{tab === 'pending' ? 'Items queued for sync will appear here for your review.' : ''}</p>
              </div>
            ) : (
              items.map(item => (
                <div
                  key={item.id}
                  className="card card-glass"
                  onClick={() => { setSelected(item); setAiExplanation(null); }}
                  style={{
                    padding: '14px 18px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: selected?.id === item.id ? 'var(--bg-card-hover)' : 'transparent',
                    transition: 'background 0.1s',
                    borderRadius: 0
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, textTransform: 'capitalize' }}>
                        {item.item_type.replace(/_/g, ' ')}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {item.direction.replace(/_/g, ' ')} · {new Date(item.created_at).toLocaleString()}
                      </div>
                    </div>
                    <span className={`badge ${tab === 'pending' ? 'badge-warning' : tab === 'approved' ? 'badge-green' : 'badge-red'}`}>
                      {tab}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Detail Panel */}
          {selected ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="card card-glass">
                <div className="card-header">
                  <span className="card-title" style={{ textTransform: 'capitalize' }}>
                    {selected.item_type.replace(/_/g, ' ')} Detail
                  </span>
                  <button className="btn btn-ghost btn-sm" onClick={() => explainItem(selected)} disabled={explaining}>
                    {explaining ? <><div className="spinner" style={{ width: 11, height: 11 }} /> Explaining</> : '✦ AI Explain'}
                  </button>
                </div>

                {aiExplanation && (
                  <div className="alert alert-info" style={{ marginBottom: 16, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                    {aiExplanation}
                  </div>
                )}

                <div style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: 14,
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  maxHeight: 260,
                  overflowY: 'auto'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(selected.source_data, null, 2)}
                  </pre>
                </div>
              </div>

              {tab === 'pending' && (
                <div className="card card-glass">
                  <div className="card-header">
                    <span className="card-title">Review Decision</span>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label className="form-label">Notes (optional)</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        placeholder="Add notes about this decision..."
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        className="btn btn-success btn-sm"
                        style={{ flex: 1, justifyContent: 'center' }}
                        disabled={acting}
                        onClick={() => handleAction(selected.id, 'approve')}
                      >
                        ✓ Approve & Sync
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ flex: 1, justifyContent: 'center' }}
                        disabled={acting}
                        onClick={() => handleAction(selected.id, 'reject')}
                      >
                        ✕ Reject
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card card-glass" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <div className="empty-state">
                <div className="empty-state-icon">✦</div>
                <p className="empty-state-desc">Select an item to review</p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
